import { Injectable } from '@angular/core';

import {
  BehaviorSubject,
  catchError,
  defer,
  exhaustMap,
  filter,
  finalize,
  interval,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  take,
  tap,
  throwError,
  timeout,
  toArray,
} from 'rxjs';
import { Angulartics2GoogleTagManager } from 'angulartics2';
import { datadogRum } from '@datadog/browser-rum';

import {
  AuthToken,
  AuthUser,
  LoginResponse,
  SignupByFirebaseProviderRequest,
  SignupByInviteRequest,
  SignupRequest,
  SignupResponse,
} from '@shared/signup/models';
import { AccountStatus } from '@shared/account/enum';
import { PaymentPlanCodes } from '@shared/price-plan/enums';
import { numberHearFromToString, SubscriptionTypes } from '@shared/restaurant/enums';
import { AlertsService } from '@tablesready/frontend-alerts-bar';
import { ApiClientService } from '@tablesready/api-client';
import { StatusService } from '@tablesready/frontend-status-bar';
import { LoggerService } from '@tablesready/frontend-logger';
import { UsernameService } from './username.service';
import { TokenModel, TokenService } from './token.service';
import { SKIP_AUTH_HTTP_HEADER_NAME } from './auth-http-interceptor.service';
import { LastLocationIdService } from './last-location-id.service';
import { GetTokenApiResponse } from './get-token-api-response.model';
import { decodeTabIdentity, TabIdentityService } from './tab-identity.service';
import { rumAccountAttributes } from './new-account-rum';

/**
 * Outcome of a single token refresh. Distinguishes a genuine auth rejection (the session is gone —
 * log out) from a transient failure (timeout / 5xx / offline — keep the session and let retry
 * recover). Collapsing every failure to `null` is what let a flaky network on resume log the user
 * out, and nuke a whole multi-location session on the first location's transient failure.
 */
export enum RefreshOutcome {
  success = 'success',
  authRejected = 'authRejected',
  transient = 'transient',
}

export interface RefreshResult {
  outcome: RefreshOutcome;
  token: AuthToken | null;
}

/** What a refresh begun before this tab's latest logout or login reports. */
const SUPERSEDED_REFRESH: Readonly<RefreshResult> = Object.freeze({
  outcome: RefreshOutcome.transient,
  token: null,
});

// TODO: rework it, should only get token and store it?
@Injectable({providedIn: 'root'})
export class AuthService {
  private msPerSec = 1000;
  private onLogoutSubject = new Subject<null>();
  private readonly tokenRefreshCheckIntervalInMilliseconds = 10000;
  public readonly onLogOut = this.onLogoutSubject.asObservable();
  public readonly tokenRefreshCheckInterval = interval(this.tokenRefreshCheckIntervalInMilliseconds)
    .pipe(
      startWith(-1)
    );
  private readonly refreshGap = 3600000;
  private refreshTokensEnabled = true;
  private readonly tokenPath = '/v.1/token';
  // Bounded waits so every gate raise has a path to a gate lower (no deadlock on a hung refresh).
  private readonly refreshGateTimeoutMs = 15000;
  private readonly refreshHttpTimeoutMs = 15000;
  // Number of token refreshes in flight (resume batch, interval batch, and the 401 fallback).
  // Authenticated requests wait via `awaitRefresh()` until this hits 0 so a request is never sent with
  // a stale / `Bearer undefined` token while tokens are rotating.
  private readonly refreshInFlight$ = new BehaviorSubject<number>(0);
  // Per-location single-flight for refreshes so concurrent refreshes for one location share one
  // result. `undefined` is a valid key: user-level sessions (zero-location users, superadmins)
  // store their token without a locationId.
  private readonly activeRefreshes = new Map<string | undefined, Observable<RefreshResult>>();
  // Bumped when this tab clears or replaces its login, so a refresh begun before then is ignored.
  private sessionGeneration = 0;

  constructor(
    private readonly http: ApiClientService,
    private readonly logger: LoggerService,
    private readonly angulartics2GoogleTagManager: Angulartics2GoogleTagManager,
    private readonly statusService: StatusService,
    private readonly alertsService: AlertsService,
    private readonly tokenService: TokenService,
    private readonly usernameService: UsernameService,
    private readonly lastLocationIdService: LastLocationIdService,
    private readonly tabIdentity: TabIdentityService,
  ) { }

  isAuthenticated(): boolean {
    // Recoverable session: true when refresh tokens exist, even if the active access token has
    // expired (getToken() would be undefined in that window). The interceptor's awaitRefresh +
    // 401-retry and the resume/health-check reconnect re-establish the access token, so treating
    // this window as "logged out" (redirect to /login, skip data load, stop refreshing lists) is
    // exactly what defeated the resume-recovery this branch is meant to add. A real logout clears
    // the store (getAll() === null), which correctly reads as unauthenticated.
    return !!this.tokenService.getAll()?.length;
  }

  login(credentials: any): Observable<AuthToken[]> {
    return this.http.post<LoginResponse>('/login', credentials).pipe(
      map(({success, tokens, error}) => {
        if (success) {
          this.setTokensAndApplyLastLocationId(tokens);

          return tokens;
        }
        throw { error: `${error}` };
      }),
      catchError(err => throwError(err.error.error || err.error)),
    );
  }

  logout(): void {
    // Lock first if another tab changed the shared login and its `storage` event is still on its way.
    this.tabIdentity.checkSharedLogin();
    // A locked tab shows another login; only Reload may change the shared login.
    if (this.tabIdentity.isLocked()) {
      return;
    }

    this.onLogoutSubject.next(null);
    this.statusService.clearAllStatuses();
    this.tokenService.clear();
    this.sessionGeneration += 1;
    this.tabIdentity.adopt();
    // NOTE: lastLocationId deliberately SURVIVES logout — it is the cross-session pointer that
    // restores the user's previous location on the next login (setTokensAndApplyLastLocationId).
    // Post-logout staleness is harmless: every refresh path bails on the cleared store
    // (getAll() === null), and a stale id at login just misses the find() and falls back.
    // Drop any in-flight single-flight refreshes so a late refresh can't rejoin a stale entry
    // (its putRefresh tap already guards against writing to the cleared store).
    this.activeRefreshes.clear();
    this.updateDataLayer();
  }

  logoutWithExpiredSession(): void {
    // Idempotent: once a logout has cleared the token store (getAll() === null), concurrent or
    // subsequent 401s must not re-trigger the alert + logout. A genuinely expired session can fail
    // many in-flight requests, and each must not produce its own "session expired" logout.
    // A locked tab never logs out (see logout()), so it raises no alert either. Checked first: a
    // rejection of this tab's login must not clear one another tab has just made.
    this.tabIdentity.checkSharedLogin();
    if (this.tokenService.getAll() === null || this.tabIdentity.isLocked()) {
      return;
    }

    this.alertsService.addSessionExpiredError('Your session has expired. Please log in again.');
    this.logout();
  }

  signup(user: SignupRequest): Observable<SignupResponse> {
    return this.http
      .post<SignupResponse>('/signup', user)
      .pipe(catchError((err) => throwError(err.error.error)));
  }

  signupByGoogle(user: SignupByFirebaseProviderRequest): Observable<SignupResponse> {
    return this.http
      .post<SignupResponse>('/signup/google', user)
      .pipe(catchError((err) => throwError(err.error.error)));
  }

  signupByInvite(inviteId: string, body: SignupByInviteRequest): Observable<string> {
    return this.http.post<string>(`/signup/${inviteId}`, body);
  }

  getToken(): string {
    return this.tokenService.getActive()?.token;
  }

  /**
   * Active location id without the destructive expiry side effect of `getActive()`. The interceptor
   * captures this BEFORE stamping a request (where `getToken()` would drop an expired active token),
   * so the 401 fallback can still re-establish that location's active token.
   */
  getActiveLocationId(): string | undefined {
    // Fall back to the persisted last-active location so the active location is still recoverable
    // after `getActive()` has dropped an expired active token (kept current on every location switch).
    return this.tokenService.getActiveLocationId() ?? this.lastLocationIdService.get() ?? undefined;
  }

  /**
   * Emits once when no token refresh is in flight (synchronously when idle). The auth interceptor
   * waits on this before sending an authenticated request, so a request never goes out mid-refresh
   * with a stale / `Bearer undefined` token. A timeout valve releases the wait if the counter ever
   * wedges, so requests can never be blocked permanently.
   */
  awaitRefresh(): Observable<void> {
    return this.refreshInFlight$.pipe(
      filter(count => count === 0),
      take(1),
      timeout({ first: this.refreshGateTimeoutMs }),
      catchError(() => of(0)),
      map(() => undefined),
    );
  }

  private gateUp(): void {
    this.refreshInFlight$.next(this.refreshInFlight$.value + 1);
  }

  private gateDown(): void {
    this.refreshInFlight$.next(Math.max(0, this.refreshInFlight$.value - 1));
  }

  refreshTokens() {
    return this.tokenRefreshCheckInterval.pipe(
      filter(() => this.refreshTokensEnabled),
      // `exhaustMap` (not `switchMap`): a batch can outlast the 10s tick, and cancelling it mid-flight
      // would re-issue refresh PUTs for a rotating token (churn → spurious failure). Ignore ticks
      // while a batch runs. The catchError lives INSIDE so an unexpected throw can't complete the
      // outer interval (which would silently stop all periodic refresh for the session).
      exhaustMap(() => this.runRefreshBatch(this.getStaleTokens()).pipe(
        catchError(() => {
          this.logoutWithExpiredSession();

          return of(null);
        }),
      )),
    );
  }

  /**
   * Refreshes a batch of tokens IN PARALLEL while holding the refresh gate up. The per-location
   * PUTs are independent (each carries its own location's bearer; the backend refresh is a
   * stateless re-sign with a per-token staleness check), so running them concurrently keeps the
   * gate window to one round-trip instead of N sequential ones — a multi-location resume used to
   * stall every authenticated request and the socket reconnect behind the whole serial batch.
   * `toArray` emits once after all complete; the gate always lowers via `finalize`, even on
   * error/cancel.
   */
  private runRefreshBatch(tokens: TokenModel[]): Observable<unknown> {
    if (!tokens.length) {
      return of(null);
    }

    this.gateUp();

    return merge(...tokens.map(token => this.refreshToken(token))).pipe(
      toArray(),
      finalize(() => this.gateDown()),
    );
  }

  /** Tokens not refreshed within the refresh gap. Shared by the interval and one-shot refreshers. */
  private getStaleTokens(): TokenModel[] {
    return (this.tokenService.getAll() ?? [])
      .filter(token => Date.now() - token.lastUpdatedAt >= this.refreshGap);
  }

  /**
   * Ensures the ACTIVE location's access token is usable for an imminent socket reconnect. First
   * refreshes any tokens older than the refresh gap (like the interval), then — if the active access
   * token is still missing — force-refreshes the active location regardless of staleness. This covers
   * a freeze SHORTER than the 1h refresh gap, where nothing is "stale" yet the short-lived access
   * token has expired, so a staleness-only refresh would refresh nothing and the reconnect would carry
   * no token. Used on tab resume and by the periodic socket health-check. Holds the refresh gate up so
   * concurrent authenticated requests wait for the rotation.
   */
  ensureActiveTokenFresh(): Observable<unknown> {
    if (!this.refreshTokensEnabled) {
      return of(null);
    }

    // Capture the active location BEFORE any getToken()/getActive() call below — getActive() drops an
    // expired active token as a side effect, which would otherwise lose the location we need to refresh.
    const activeLocationId = this.getActiveLocationId();

    return this.runRefreshBatch(this.getStaleTokens()).pipe(
      switchMap(() => {
        // If refreshing stale tokens already restored the active access token, we're done.
        if (this.getToken()) {
          return of(null);
        }

        return this.refreshActiveToken(activeLocationId).pipe(
          tap(result => {
            if (result.outcome === RefreshOutcome.authRejected) {
              // The refresh token itself was rejected — surface the expired session NOW. Without
              // this, the resume/health-check path (whose caller swallows errors by design) would
              // show a misleading "connection problem" banner and hammer a doomed refresh every
              // tick for up to an hour until the interval-staleness path finally logs out.
              this.logoutWithExpiredSession();
            }
          }),
        );
      }),
    );
  }

  setTokensAndApplyLastLocationId(tokens: (AuthToken & {lastUpdatedAt?: number})[], forceLocationId?: string): void {
    // A login that lands after the tab locked must not replace the shared login; only Reload may.
    if (this.tabIdentity.isLocked()) {
      return;
    }

    this.sessionGeneration += 1;
    this.tokenService.setTokens(tokens);
    const lastLocationId = forceLocationId ? forceLocationId : this.lastLocationIdService.get();

    const tokensWithLocation = tokens.filter(token => !!token.locationId);
    if (tokensWithLocation.length) {
      const tokenByLastLocationId = tokensWithLocation.find(token => token.locationId === lastLocationId);
      const locationId = tokenByLastLocationId?.locationId || tokensWithLocation[0].locationId;
      this.tokenService.setActiveByLocationId(locationId);
      this.lastLocationIdService.set(locationId);
    } else {
      this.lastLocationIdService.remove();
      this.tokenService.setActive(tokens[0]);
    }

    this.tabIdentity.adopt();
  }

  loginByToken(token: string, locationId: string): Observable<AuthToken[]> {
    console.log({ token });
    return this.http.post<LoginResponse>(
      `${this.tokenPath}/tokenize`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          [SKIP_AUTH_HTTP_HEADER_NAME]: 'true',
        },
      },
    )
      .pipe(
        map(({ success, tokens, error }) => {
          if (success) {
            return tokens;
          }

          throw { error: `${error}` };
        }),
        tap((tokens) => {
          this.disableRefreshingTokens();
          this.setTokensAndApplyLastLocationId(tokens, locationId);
        }),
        catchError(err => throwError(err.error.error || err.error))
      );
  }

  loginByGoogle(credentials: any): Observable<AuthToken[]> {
    return this.http.post<LoginResponse>('/login/google', credentials).pipe(
      map(({ success, tokens, error }) => {
        if (success) {
          this.setTokensAndApplyLastLocationId(tokens);

          return tokens;
        }
        throw { error: `${error}` };
      }),
      catchError(err => throwError(err.error.error || err.error)),
    );
  }

  /**
   * Single-flight refresh of one location's token, shared by the resume/interval batch AND the 401
   * fallback so the same rotating refresh token is never refreshed twice concurrently (which would
   * fail). Holds the gate up while in flight; no auto-logout (callers decide); returns the refreshed
   * token so retry callers do not have to read whichever location is active later.
   */
  private refreshLocation(locationId: string | undefined): Observable<RefreshResult> {
    // `defer` so the single-flight bookkeeping (map lookup, gate raise, PUT) runs at SUBSCRIBE
    // time, not at pipeline construction — otherwise a batch would raise the gate and populate the
    // map for every token up front.
    return defer(() => {
      // Tokenized sessions (loginByToken → disableRefreshingTokens, e.g. admin impersonation) must
      // never rotate the real user's token — the backend refresh persists lastToken/lastLogin for
      // that user, and the session is meant to decay at access-token expiry. Report a rejection so
      // the 401 path ends the session exactly like it did before the refresh flow existed.
      if (!this.refreshTokensEnabled) {
        return of<RefreshResult>({ outcome: RefreshOutcome.authRejected, token: null });
      }

      const existing = this.activeRefreshes.get(locationId);
      if (existing) {
        return existing;
      }

      const all = this.tokenService.getAll();
      if (all === null) {
        // Store cleared (logged out) — nothing to refresh, and no logout to trigger.
        return of<RefreshResult>({ outcome: RefreshOutcome.transient, token: null });
      }

      const token = all.find(_token => _token.locationId === locationId);
      if (!token) {
        // No refresh token for this location — the session for it is genuinely gone.
        return of<RefreshResult>({ outcome: RefreshOutcome.authRejected, token: null });
      }

      this.gateUp();

      const generation = this.sessionGeneration;
      const refresh$ = this.putRefresh(token, generation).pipe(
        map<AuthToken, RefreshResult>(refreshed => this.tokenService.getAll() === null
          // A logout cleared the store while this refresh was in flight — benign, don't log out again.
          ? { outcome: RefreshOutcome.transient, token: null }
          : { outcome: RefreshOutcome.success, token: refreshed }),
        catchError((err: unknown) => of<RefreshResult>({
          // Only a 401/403 means the refresh token itself is rejected (session gone). Timeout / 5xx /
          // status-0 (offline) are transient — keep the session and let retry/health-check recover.
          outcome: this.isAuthRejection(err) ? RefreshOutcome.authRejected : RefreshOutcome.transient,
          token: null,
        })),
        // This tab logged out or in while the PUT was in flight, so the result is the old session's:
        // no token to retry with, and no rejection that would log the new session out.
        map(result => (this.sessionChangedSince(generation) ? SUPERSEDED_REFRESH : result)),
        finalize(() => {
          this.gateDown();
          this.activeRefreshes.delete(locationId);
        }),
        shareReplay(1),
      );

      this.activeRefreshes.set(locationId, refresh$);

      return refresh$;
    });
  }

  private sessionChangedSince(generation: number): boolean {
    return generation !== this.sessionGeneration;
  }

  /** Whether the token stored for `refreshed`'s location belongs to the user `refreshed` was issued to. */
  private isStoredForSameUser(refreshed: AuthToken): boolean {
    const stored = this.tokenService.getAll()?.find(token => token.locationId === refreshed.locationId);
    const storedUserId = decodeTabIdentity(stored?.token)?.userId;

    return storedUserId !== undefined && storedUserId === decodeTabIdentity(refreshed.token)?.userId;
  }

  /** A refresh failure is a genuine auth rejection only on 401/403; everything else is transient. */
  private isAuthRejection(err: unknown): boolean {
    const status = (err as { status?: number })?.status;
    return status === 401 || status === 403;
  }

  /**
   * Raw token refresh PUT — bounded by a timeout so the refresh gate is always released. The refreshed
   * token is stored for its location, but only re-established as active if that location is still active
   * when the refresh completes.
   */
  private putRefresh(token: TokenModel, generation: number): Observable<AuthToken> {
    return this.http.put<AuthToken>(
      `${this.tokenPath}/refresh`,
      { tokenLastUpdatedAt: token.lastUpdatedAt },
      {
        headers: {
          Authorization: `Bearer ${token.token}`,
          [SKIP_AUTH_HTTP_HEADER_NAME]: 'true',
        },
      })
      .pipe(
        timeout(this.refreshHttpTimeoutMs),
        tap(refreshed => {
          // Another tab's login may already be stored while its `storage` event is still on its way.
          this.tabIdentity.checkSharedLogin();
          // A logout cleared the store while this refresh was in flight — don't resurrect a single
          // token into an otherwise logged-out session. A tab that locked, or logged in again,
          // meanwhile writes nothing, and nothing overwrites another user's token.
          if (this.tokenService.getAll() === null || this.tabIdentity.isLocked() ||
            this.sessionChangedSince(generation) || !this.isStoredForSameUser(refreshed)) {
            return;
          }

          this.tokenService.update(refreshed);
          if (refreshed.locationId === this.getActiveLocationId()) {
            this.tokenService.setActive(refreshed);
          }
        }),
      );
  }

  private refreshToken(token: TokenModel): Observable<RefreshResult> {
    // Resume/interval refresher: single-flight refresh. Log out ONLY on a genuine auth rejection — a
    // transient failure (offline/timeout/5xx) must keep the session, otherwise a flaky network on
    // resume, or one location's blip in a multi-location batch, logs the whole user out.
    return this.refreshLocation(token.locationId).pipe(
      tap(result => {
        if (result.outcome === RefreshOutcome.authRejected) {
          // TODO: remove single token instead of logout?
          this.logoutWithExpiredSession();
        }
      }),
    );
  }

  /**
   * 401 fallback: refresh the active location's token (regardless of staleness) and return the result
   * so the interceptor retries with the captured location's credentials (success), logs out (genuine
   * rejection) or rethrows without logout (transient). Shares the per-location single-flight, so if the
   * resume batch is already refreshing this location it joins that refresh instead of colliding.
   *
   * `undefined` is a legitimate input: user-level sessions (zero-location users, superadmins in the
   * admin app) store their token without a locationId, and refreshLocation matches it the same way —
   * short-circuiting these to a rejection would log them out on the first 401 instead of recovering.
   * A genuinely unknown location still resolves to authRejected there (no matching token).
   */
  refreshActiveToken(locationId: string | undefined): Observable<RefreshResult> {
    return this.refreshLocation(locationId);
  }

  updateDataLayer(user?: AuthUser): void {
    if (this.isAuthenticated() && user && user.location) {
      const { account, location } = user;
      const userId = location.mongoId || location._id;

      this.angulartics2GoogleTagManager.setUsername(userId);
      const created_at = user.created_date
        ? Math.round(user.created_date / this.msPerSec)
        : null;

      let subscriptionType = account.subscriptionType as string;
      if (subscriptionType !== SubscriptionTypes.offline) {
        if(account.recurly?.trial) {
          subscriptionType = 'Trial';
        } else if (location.recurly?.planCode === PaymentPlanCodes.Free) {
          subscriptionType = 'Free';
        }
      }

      const newData: any = {
        event: 'user',
        userId,
        logged_in: true,
        created_at,
        name: location.name,
        email: user.email,
        fullName: user.name,
        subscription_type: subscriptionType,
        expires_at: Math.round(location.expiration_date / this.msPerSec),
        country: location.country || '',
        recurly_state: location.recurly
          ? location.recurly.subscription_state
          : 'not_applicable',
        recurly_email: account.recurly ? account.recurly.email : '',
        recurly_account: account.recurly ? account.recurly.account_code : '',
        enabled: location.enabled,
        domain: window.location.hostname,
        industry: account.industry ? account.industry : '',
        messages_month: location.usage?.total || 0,
        verified: account.status === AccountStatus.verified,
        smsConfirmation: location.messages.smsConfirmation?.enabled || false,
        smsResConfirmation: location.messages.smsResConfirmation?.enabled || false,
        smsCompleted: location.messages.smsCompleted?.enabled || false,
        smsReply: location.messages.smsReply?.enabled || false,
        smsReservationCancellation: location.messages.smsReservationCancellation.enabled || false,
        smsNext: location.messages.smsNext?.enabled || false,
        smsReminder: location.messages.smsReminder?.enabled || false,
        squareEnabled: location.integrations?.square || false,
        hearFrom: account.howDidYouHearAboutUs
          ? {
            hearFrom: numberHearFromToString[account.howDidYouHearAboutUs.hearFrom],
            otherSource: account.howDidYouHearAboutUs.otherSource,
          }
          : null,
        account_id: account.id,
        account_name: account.name,
      };

      const datadogRumData: any = {
        id: userId,
        name: newData.name,
        email: newData.email,
        ...rumAccountAttributes(
          account,
          location.country,
          subscriptionType,
          location.recurly?.planCode ?? null,
          Date.now(),
        ),
      };

      this.angulartics2GoogleTagManager.pushLayer(newData);
      datadogRum.setUser(datadogRumData);
    } else {
      this.angulartics2GoogleTagManager.setUsername(null);

      this.angulartics2GoogleTagManager.pushLayer({
        event: 'user',
        userId: null,
        logged_in: false,
        email: null,
        created_at: null,
        name: null,
        subscription_type: null,
        expires_at: null,
        country: null,
        recurly_state: null,
        recurly_email: null,
        recurly_account: null,
        enabled: null,
        domain: null,
        industry: null,
        messages_month: null,
        verified: null,
        smsConfirmation: null,
        smsResConfirmation: null,
        smsCompleted: null,
        smsReply: null,
        smsReservationCancellation: null,
        smsNext: null,
        smsReminder: null,
        squareEnabled: null,
      });
    }
  }

  passwordReset(email: string, domain: string) {
    return this.http.post('/passwordReset', { email, domain })
      .pipe(
        catchError(() => throwError('Error resetting password.')),
        map((result: any) => {
          if (!result.success) {
            throw result.error;
          }
        }),
      );
  }

  getAnonToken(username: string = this.usernameService.getUsername()) {
    const token = this.tokenService.getAnonToken();
    if (token) {
      return of(token);
    }

    return this.getApiAnonToken(username).pipe(
      tap(result => this.tokenService.setAnonToken(result.token, result.expiresIn)),
      map(result => result.token),
    );
  }

  disableRefreshingTokens() {
    this.refreshTokensEnabled = false;
  }

  private getApiAnonToken(username: string): Observable<GetTokenApiResponse> {
    return this.http.get<GetTokenApiResponse>(`/public/token/${username}`).pipe(
      map(result => {
        if (!result.success) {
          throw result.error;
        }
        return result;
      }),
      map((result: GetTokenApiResponse) => {
        // Translate it in ms units
        return { ...result, expiresIn: result.expiresIn * 1000 };
      }),
      catchError(error => {
        this.logger.warn('error getting public token:', error);
        return throwError(error);
      })
    );
  }
}
